import {
  FormCheckbox,
  FormDateDiapasons,
  FormDatePicker,
  FormDateRangePicker,
  FormImage,
  FormImageUpload,
  FormInput,
  FormInputCurrency,
  FormInputNumber,
  FormInputPercent,
  FormLink,
  FormSelect,
  FormTextarea,
  FormTreeSelect,
} from 'components';

import { TFieldProps } from 'types';

export const FieldByType = ({ field }: { field: TFieldProps }) => {
  const {
    min,
    max,
    step,
    link,
    type,
    mode,
    name,
    extra,
    label,
    rules,
    prefix,
    hidden,
    tooltip,
    options,
    loading,
    readOnly,
    isToggle,
    autoSize,
    imgWidth,
    showTime,
    disabled,
    treeData,
    precision,
    showCount,
    maxLength,
    showSearch,
    addonAfter,
    entityType,
    allowClear,
    fieldNames,
    maxTagCount,
    inputStyles,
    placeholder,
    dependencies,
    itemClassName,
    maxTagPlaceholder,
    normalize,
    tagRender,
    onDeselect,
    onDateChange,
    onTextChange,
    optionRender,
    onSelectChange,
    onCheckboxChange,
    onDateRangeChange,
    renderCustomField,
  } = field;

  switch (type) {
    case TFieldProps.type.Text:
      return (
        <FormInput
          name={name}
          label={label}
          rules={rules}
          prefix={prefix}
          hidden={hidden}
          tooltip={tooltip}
          disabled={disabled}
          addonAfter={addonAfter}
          allowClear={allowClear}
          placeholder={placeholder}
          inputStyles={inputStyles}
          itemClassName={itemClassName}
          normalize={normalize}
          onTextChange={onTextChange}
        />
      );
    case TFieldProps.type.Number:
      return (
        <FormInputNumber
          min={min}
          max={max}
          name={name}
          label={label}
          rules={rules}
          prefix={prefix}
          hidden={hidden}
          tooltip={tooltip}
          isToggle={isToggle}
          disabled={disabled}
          precision={precision}
          addonAfter={addonAfter}
          placeholder={placeholder}
          inputStyles={inputStyles}
          itemClassName={itemClassName}
        />
      );
    case TFieldProps.type.Currency:
      return (
        <FormInputCurrency
          name={name}
          label={label}
          rules={rules}
          tooltip={tooltip}
          disabled={disabled}
          addonAfter={addonAfter}
          placeholder={placeholder}
          inputStyles={inputStyles}
          itemClassName={itemClassName}
        />
      );
    case TFieldProps.type.Percent:
      return (
        <FormInputPercent
          min={min}
          max={max}
          step={step}
          name={name}
          label={label}
          rules={rules}
          tooltip={tooltip}
          disabled={disabled}
          precision={precision}
          addonAfter={addonAfter}
          placeholder={placeholder}
          inputStyles={inputStyles}
          dependencies={dependencies}
          itemClassName={itemClassName}
        />
      );
    case TFieldProps.type.Select:
      return (
        <FormSelect
          name={name}
          mode={mode}
          extra={extra}
          label={label}
          rules={rules}
          options={options}
          loading={loading}
          tooltip={tooltip}
          disabled={disabled}
          allowClear={allowClear}
          showSearch={showSearch}
          fieldNames={fieldNames}
          placeholder={placeholder}
          inputStyles={inputStyles}
          maxTagCount={maxTagCount}
          itemClassName={itemClassName}
          normalize={normalize}
          tagRender={tagRender}
          onDeselect={onDeselect}
          optionRender={optionRender}
          onSelectChange={onSelectChange}
        />
      );
    case TFieldProps.type.TreeSelect:
      return (
        <FormTreeSelect
          name={name}
          label={label}
          rules={rules}
          loading={loading}
          tooltip={tooltip}
          treeData={treeData}
          disabled={disabled}
          allowClear={allowClear}
          showSearch={showSearch}
          placeholder={placeholder}
          inputStyles={inputStyles}
          itemClassName={itemClassName}
          maxTagCount={maxTagCount}
          maxTagPlaceholder={maxTagPlaceholder}
          normalize={normalize}
        />
      );
    case TFieldProps.type.DatePicker:
      return (
        <FormDatePicker
          name={name}
          label={label}
          rules={rules}
          extra={extra}
          tooltip={tooltip}
          showTime={showTime}
          disabled={disabled}
          placeholder={placeholder}
          inputStyles={inputStyles}
          itemClassName={itemClassName}
          onDateChange={onDateChange}
        />
      );
    case TFieldProps.type.DateRangePicker:
      return (
        <FormDateRangePicker
          name={name}
          label={label}
          rules={rules}
          tooltip={tooltip}
          disabled={disabled}
          itemClassName={itemClassName}
          onDateRangeChange={onDateRangeChange}
        />
      );
    case TFieldProps.type.DateDiapasons:
      return (
        <FormDateDiapasons
          name={name}
          label={label}
          rules={rules}
          tooltip={tooltip}
          disabled={disabled}
          itemClassName={itemClassName}
        />
      );
    case TFieldProps.type.Image:
      return (
        <FormImage
          name={name}
          label={label}
          rules={rules}
          tooltip={tooltip}
          imgWidth={imgWidth}
          inputStyles={inputStyles}
          itemClassName={itemClassName}
        />
      );
    case TFieldProps.type.ImageUpload:
      return (
        <FormImageUpload
          name={name}
          label={label}
          rules={rules}
          tooltip={tooltip}
          imgWidth={imgWidth}
          entityType={entityType}
          inputStyles={inputStyles}
          itemClassName={itemClassName}
        />
      );
    case TFieldProps.type.Textarea:
      return (
        <FormTextarea
          name={name}
          label={label}
          rules={rules}
          readOnly={readOnly}
          allowClear={allowClear}
          tooltip={tooltip}
          autoSize={autoSize}
          disabled={disabled}
          showCount={showCount}
          maxLength={maxLength}
          placeholder={placeholder}
          inputStyles={inputStyles}
          itemClassName={itemClassName}
        />
      );
    case TFieldProps.type.Checkbox:
      return (
        <FormCheckbox
          name={name}
          label={label}
          extra={extra}
          rules={rules}
          hidden={hidden}
          tooltip={tooltip}
          disabled={disabled}
          inputStyles={inputStyles}
          itemClassName={itemClassName}
          onCheckboxChange={onCheckboxChange}
        />
      );
    case TFieldProps.type.Link:
      return (
        <FormLink
          name={name}
          link={link}
          label={label}
          rules={rules}
          hidden={hidden}
          tooltip={tooltip}
        />
      );
    case TFieldProps.type.Custom:
      return renderCustomField?.({ name });
    default:
      return null;
  }
};
